import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteWorkTemplate, getWorkTemplates, patchWorkTemplate, postWorkTemplate } from './api';
import { workTemplateKeys } from './keys';
import { PatchWorkTemplateBody, PostWorkTemplateBody } from './types';

export const useWorkTemplatesQuery = ({
    centerId,
    enabled = true,
}: {
    centerId: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: workTemplateKeys.list(centerId),
        queryFn: () => getWorkTemplates({ centerId }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data,
    });

export const usePostWorkTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, body }: { centerId: string; body: PostWorkTemplateBody }) =>
            postWorkTemplate({ centerId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: workTemplateKeys.list(variables.centerId) });
        },
    });
};

export const usePatchWorkTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            templateId,
            body,
        }: {
            centerId: string;
            templateId: string;
            body: PatchWorkTemplateBody;
        }) => patchWorkTemplate({ centerId, templateId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: workTemplateKeys.list(variables.centerId) });
        },
    });
};

export const useDeleteWorkTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, templateId }: { centerId: string; templateId: string }) =>
            deleteWorkTemplate({ centerId, templateId }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: workTemplateKeys.list(variables.centerId) });
        },
    });
};
